import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { UserRole } from '@/graphql/gql-types';
import type { User } from '@/graphql/gql-types';
import { labels } from '@/locales/en.label';
import {
	type ColumnFiltersState,
	type SortingState,
	type VisibilityState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useUsersList } from '../../hooks/use-users-list';
import { columns } from './admin-table-column';

export const RoleOptions = [
	{ label: 'Admin', value: UserRole.admin },
	{ label: 'Corp Full', value: UserRole.corp_full },
	{ label: 'Corp Read', value: UserRole.corp_read },
	{ label: 'Hotel Full', value: UserRole.hotel_full },
	{ label: 'User', value: UserRole.user },
];

export function AdminUserTable() {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = useState({});
	const [globalFilter, setGlobalFilter] = useState('');
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 10,
	});

	const { usersList, isLoading } = useUsersList();

	// Simulate loading state
	const simulateLoading = () => {
		setLoading(true);
		setTimeout(() => {
			setLoading(false);
		}, 1000);
	};

	useEffect(() => {
		if (isLoading) {
			simulateLoading();
		}
	}, [isLoading]);

	const data = (usersList ?? []) as User[];

	const table = useReactTable({
		data: data,
		columns,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onPaginationChange: setPagination,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			globalFilter,
			pagination,
		},
	});
	return (
		<div className='tw-space-y-4'>
			<div className='tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-items-center tw-justify-between'>
				<div className='tw-relative tw-w-full sm:tw-w-72'>
					<Search className='tw-absolute tw-left-2 tw-top-2.5 tw-h-4 tw-w-4 tw-text-muted-foreground' />
					<Input
						placeholder={labels.admin.user_table.placeholder.search_users}
						value={globalFilter ?? ''}
						onChange={(e) => setGlobalFilter(e.target.value)}
						className='tw-pl-8'
					/>
				</div>
				<div className='tw-flex tw-items-center tw-gap-2 tw-w-full sm:tw-w-auto'>
					<Select
						value={(table.getColumn('role')?.getFilterValue() as string) || ''}
						onValueChange={(value: any) => {
							simulateLoading();
							table.getColumn('role')?.setFilterValue(value || undefined);
						}}
					>
						<SelectTrigger className='tw-w-full sm:tw-w-[180px]'>
							<SelectValue
								placeholder={labels.admin.user_table.placeholder.filter_by_role}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='all'>
								{labels.admin.user_table.text.all}
							</SelectItem>
							{RoleOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className='tw-rounded-md tw-border tw-w-full overflow-x-auto'>
				<Table className=''>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead
										className='tw-text-center tw-font-medium tw-max-w-[160px]'
										key={header.id}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{loading ? (
							Array.from({ length: pagination.pageSize }).map((_, index) => (
								<TableRow key={`loading-${index}`}>
									{Array.from({ length: columns.length }).map(
										(_, cellIndex) => (
											<TableCell key={`loading-cell-${cellIndex}`}>
												<Skeleton className='tw-h-6 tw-w-full' />
											</TableCell>
										),
									)}
								</TableRow>
							))
						) : data.length > 0 ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell className=' tw-text-center' key={cell.id}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className='tw-h-24 tw-text-center'
								>
									{labels.admin.user_table.text.no_users_found}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<div className='tw-flex tw-items-center tw-justify-between'>
				<div className='tw-flex-1 tw-text-sm tw-text-muted-foreground'>
					Showing {table.getFilteredRowModel().rows.length} of{' '}
					{usersList?.length} users
				</div>
				<div className='tw-flex tw-items-center tw-gap-4'>
					<Select
						value={`${table.getState().pagination.pageSize}`}
						onValueChange={(value: any) => {
							simulateLoading();
							table.setPageSize(Number(value));
						}}
					>
						<SelectTrigger className='tw-h-8 '>
							<SelectValue placeholder={table.getState().pagination.pageSize} />
						</SelectTrigger>
						<SelectContent side='top'>
							{[5, 10, 20, 50].map((pageSize) => (
								<SelectItem key={pageSize} value={`${pageSize}`}>
									{pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className='tw-flex tw-items-center tw-gap-4'>
						<Button
							variant='outline'
							size='sm'
							onClick={() => {
								simulateLoading();
								table.previousPage();
							}}
							disabled={!table.getCanPreviousPage()}
						>
							{labels.admin.user_table.btn.previous}
						</Button>
						<div className='tw-text-sm tw-h-8 tw-w-[80px] tw-flex tw-items-center'>
							Page {table.getState().pagination.pageIndex + 1} of{' '}
							{table.getPageCount()}
						</div>
						<Button
							variant='outline'
							size='sm'
							onClick={() => {
								simulateLoading();
								table.nextPage();
							}}
							disabled={!table.getCanNextPage()}
						>
							{labels.admin.user_table.btn.next}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
